import { useState } from 'react';
import { omit } from 'lodash';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { makeStyles } from '@material-ui/core';
import { darken, lighten, alpha } from '@material-ui/core/styles/colorManipulator';
import ListIcon from '@material-ui/icons/List';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import MenuIcon from '@material-ui/icons/Menu';
import { Trans, t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { PANEL_CATEGORIES, STATUS_LIST_PANEL_PREVIEW_IMAGE } from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import { useId, useOptions } from '../../page-framework/panel/Panel';
import { useDashboardContext } from '../../page-framework';
import AgentTests from '../../service-delivery/AgentTests';
import PerformanceList from '../../service-delivery/PerformanceList';
import AgentProperties from '../../service-delivery/AgentProperties';
import useIsPrinting from '../../../utilities/PrintUtilities';
import { usePageStateValue } from '../../../stores/PageStateStore';
import {
    agentFilterId,
    businessServiceFilterId,
    statusFilterId,
    testTypeFilterId,
    timeRangeFilterId,
} from '../../filters';

const STATUS_TITLE = t`View the status trend for Service Tests being run by this nPoint`;
const PERFORMANCE_TITLE = t`View performance charts for Service Tests being run by this nPoint`;
const PROPERTIES_TITLE = t`View properties for this nPoint`;

const TAB_TEST_AGENTS = 0;
const TAB_PERFORMANCE = 1;
const TAB_PROPERTIES = 2;
const DEFAULT_TAB = TAB_TEST_AGENTS;

const title = t`nPoint Service Tests`;

const performanceDataSource = {
    url: '/query/table',
    params: {
        type: 'agent,test',
        entityFields: 'name,tags',
        metrics: 'status,availPercent,*primary*',
        rowLimit: 10,
        rowStart: 0,
        sort: '-status',
        trends: 'status,availability,*,*thresholds*',
    },
};

const useStyles = makeStyles((theme) => ({
    tab: {
        '& .MuiTab-wrapper': {
            flexDirection: 'row',

            '& svg': {
                marginRight: theme.spacing(1),
            },
        },
    },
    tabs: {
        borderBottom: `1px solid ${
            theme.palette.type === 'light'
                ? lighten(alpha(theme.palette.divider, 1), 0.85)
                : darken(alpha(theme.palette.divider, 1), 0.85)
        }`,
        marginBottom: theme.spacing(1),

        '& .MuiTab-labelIcon': {
            minHeight: '50px',

            '& .MuiTab-wrapper > *:first-child': {
                marginBottom: 0,
            },
        },
    },
}));

function AgentDetailPanel() {
    const classes = useStyles();
    const isPrinting = useIsPrinting();
    const id = useId();
    const { editing } = useDashboardContext();
    const [options, setOptions] = useOptions();
    const tabKey = `${id}-Tab`;
    const [selectedTab, pageStateActions] = usePageStateValue({
        key: tabKey,
        defaultValue: options?.selectedTab || DEFAULT_TAB,
    });
    const [workingOptions, setWorkingOptions] = useState({
        selectedTab,
    });

    const handleTabChange = (tab) => {
        if (tab === null) return;

        const isDefaultTab = tab === DEFAULT_TAB;
        const newOptions = isDefaultTab
            ? omit(options || {}, 'selectedTab')
            : {
                  ...(options || {}),
                  selectedTab: tab,
              };

        if (editing) {
            setOptions(newOptions);
        } else {
            setWorkingOptions(newOptions);

            // If the selected tab is the same as the options value, or if the selected tab is the default tab
            // and no options value is present, we want to clear the page state to keep the URL clean.  The
            // URL value is only necessary if the working tab is different from the default or the options value.
            const clearPageState = tab === options?.selectedTab || (isDefaultTab && !options?.selectedTab);

            if (clearPageState) {
                pageStateActions.clearValue(tabKey);
            } else {
                pageStateActions.setValue(tabKey, tab);
            }
        }
    };

    const tab = (editing ? options?.selectedTab : workingOptions.selectedTab) || DEFAULT_TAB;

    return (
        <>
            {!isPrinting && (
                <Tabs
                    className={classes.tabs}
                    value={tab}
                    onChange={(e, index) => handleTabChange(index)}
                    aria-label="agent detail tabs"
                >
                    <Tab
                        className={classes.tab}
                        id="agent-detail-tab-status"
                        icon={<ListIcon />}
                        label={<Trans>Status</Trans>}
                        title={STATUS_TITLE}
                    />
                    <Tab
                        className={classes.tab}
                        id="agent-detail-tab-performance"
                        icon={<EqualizerIcon />}
                        label={<Trans>Performance</Trans>}
                        title={PERFORMANCE_TITLE}
                    />
                    <Tab
                        className={classes.tab}
                        id="agent-detail-tab-properties"
                        icon={<MenuIcon />}
                        label={<Trans>Properties</Trans>}
                        title={PROPERTIES_TITLE}
                    />
                </Tabs>
            )}
            {tab === TAB_TEST_AGENTS && <AgentTests />}
            {tab === TAB_PERFORMANCE && <PerformanceList dataSource={performanceDataSource} />}
            {tab === TAB_PROPERTIES && <AgentProperties />}
        </>
    );
}

AgentDetailPanel.propTypes = panelPropTypes;

AgentDetailPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    minContentHeight: 125,
    contentHeight: 375,
    autoHeight: true,
};

export default Panel({
    typeAlias: 'TestsForAnAgent',
    catalogEntry: {
        category: PANEL_CATEGORIES.AGENT,
        title,
        description: t`List of service tests running on a selected nPoint, showing status and performance trends`,
        previewImage: STATUS_LIST_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId, agentFilterId],
        optional: [businessServiceFilterId, statusFilterId, testTypeFilterId],
    },
})(AgentDetailPanel);
